import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { PanelBottom, Plus, Pencil, Trash2 } from "lucide-react";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminSection } from "@/components/admin/AdminSection";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  listFooter,
  saveFooterColumn,
  deleteFooterColumn,
  saveFooterLink,
  deleteFooterLink,
} from "@/lib/admin/cms.functions";

export const Route = createFileRoute("/_admin/admin/cms/footer")({
  component: FooterCmsPage,
});

type Column = { id: string; title: string; display_order: number; is_visible: boolean };
type LinkRow = {
  id: string;
  column_id: string;
  label: string;
  href: string;
  is_external: boolean;
  is_visible: boolean;
  display_order: number;
};

type ColumnDraft = { id?: string; title: string; display_order: number; is_visible: boolean };
type LinkDraft = {
  id?: string;
  column_id: string;
  label: string;
  href: string;
  is_external: boolean;
  is_visible: boolean;
  display_order: number;
};

function FooterCmsPage() {
  const list = useServerFn(listFooter);
  const saveCol = useServerFn(saveFooterColumn);
  const delCol = useServerFn(deleteFooterColumn);
  const saveLink = useServerFn(saveFooterLink);
  const delLink = useServerFn(deleteFooterLink);
  const qc = useQueryClient();

  const [colDraft, setColDraft] = useState<ColumnDraft | null>(null);
  const [linkDraft, setLinkDraft] = useState<LinkDraft | null>(null);

  const { data, isLoading } = useQuery({ queryKey: ["cms-admin", "footer"], queryFn: () => list() });
  const columns = useMemo(
    () => ((data?.columns ?? []) as unknown as Column[]).sort((a, b) => a.display_order - b.display_order),
    [data],
  );
  const links = useMemo(() => (data?.links ?? []) as unknown as LinkRow[], [data]);

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["cms-admin", "footer"] });
    void qc.invalidateQueries({ queryKey: ["cms", "footer"] });
  };

  const colMutation = useMutation({
    mutationFn: (d: ColumnDraft) => saveCol({ data: d }),
    onSuccess: () => { toast.success("Column saved"); setColDraft(null); refresh(); },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "Save failed"),
  });
  const linkMutation = useMutation({
    mutationFn: (d: LinkDraft) => saveLink({ data: d }),
    onSuccess: () => { toast.success("Link saved"); setLinkDraft(null); refresh(); },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "Save failed"),
  });
  const delColMutation = useMutation({
    mutationFn: (id: string) => delCol({ data: { id } }),
    onSuccess: () => { toast.success("Column removed"); refresh(); },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "Couldn't delete the column."),
  });
  const delLinkMutation = useMutation({
    mutationFn: (id: string) => delLink({ data: { id } }),
    onSuccess: () => { toast.success("Link removed"); refresh(); },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "Couldn't delete the link."),
  });

  return (
    <div className="space-y-4">
      <AdminPageHeader
        title="Footer Management"
        description="Footer link columns shown on every public page. Bottom-bar text and toggles live in Website Settings."
      />
      <div className="flex justify-end">
        <Button
          size="sm"
          className="gap-1"
          onClick={() => setColDraft({ title: "", display_order: columns.length, is_visible: true })}
        >
          <Plus className="h-3.5 w-3.5" aria-hidden /> Add column
        </Button>
      </div>

      {isLoading ? <p className="text-sm text-muted-foreground">Loading footer…</p> : null}
      {!isLoading && columns.length === 0 ? (
        <p className="text-sm text-muted-foreground">No footer columns yet — add one to get started.</p>
      ) : null}

      {columns.map((col, i) => {
        const colLinks = links
          .filter((l) => l.column_id === col.id)
          .sort((a, b) => a.display_order - b.display_order);
        return (
          <AdminSection
            key={col.id}
            number={i + 1}
            icon={PanelBottom}
            title={col.title}
            description={`${colLinks.length} link${colLinks.length === 1 ? "" : "s"}${col.is_visible ? "" : " · hidden"}`}
            customised={colLinks.length > 0}
            actions={
              <div className="flex gap-1">
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-1"
                  onClick={() =>
                    setLinkDraft({
                      column_id: col.id,
                      label: "",
                      href: "/",
                      is_external: false,
                      is_visible: true,
                      display_order: colLinks.length,
                    })
                  }
                >
                  <Plus className="h-3.5 w-3.5" aria-hidden /> Link
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => setColDraft({ id: col.id, title: col.title, display_order: col.display_order, is_visible: col.is_visible })}
                >
                  <Pencil className="h-4 w-4" aria-hidden />
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="icon" variant="ghost">
                      <Trash2 className="h-4 w-4 text-destructive" aria-hidden />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete this column?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This removes "{col.title}" and all its links permanently. This action cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => delColMutation.mutate(col.id)}>
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            }
          >
            {colLinks.length === 0 ? (
              <p className="text-sm text-muted-foreground">No links in this column.</p>
            ) : (
              <ul className="divide-y rounded-md border">
                {colLinks.map((l) => (
                  <li key={l.id} className="flex flex-wrap items-center gap-2 px-3 py-2">
                    <span className="min-w-32 flex-1 text-sm text-foreground">{l.label}</span>
                    <span className="min-w-32 flex-1 truncate text-xs text-muted-foreground">{l.href}</span>
                    {!l.is_visible ? (
                      <span className="rounded border px-1.5 text-[10px] uppercase text-muted-foreground">Hidden</span>
                    ) : null}
                    <div className="ml-auto flex gap-1">
                      <Button size="icon" variant="ghost" onClick={() => setLinkDraft({ ...l })}>
                        <Pencil className="h-4 w-4" aria-hidden />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="icon" variant="ghost">
                            <Trash2 className="h-4 w-4 text-destructive" aria-hidden />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete this link?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This removes "{l.label}" permanently. This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => delLinkMutation.mutate(l.id)}>
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </AdminSection>
        );
      })}

      <Dialog open={colDraft !== null} onOpenChange={(o) => !o && setColDraft(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{colDraft?.id ? "Edit column" : "Add column"}</DialogTitle>
          </DialogHeader>
          {colDraft ? (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="col-title" className="text-xs">Column title</Label>
                <Input id="col-title" value={colDraft.title} onChange={(e) => setColDraft({ ...colDraft, title: e.target.value })} />
              </div>
              <div className="flex items-center justify-between rounded-md border p-3">
                <span className="text-sm">Visible on site</span>
                <Switch checked={colDraft.is_visible} onCheckedChange={(v) => setColDraft({ ...colDraft, is_visible: v })} />
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setColDraft(null)}>Cancel</Button>
            <Button disabled={!colDraft?.title.trim() || colMutation.isPending} onClick={() => colDraft && colMutation.mutate(colDraft)}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={linkDraft !== null} onOpenChange={(o) => !o && setLinkDraft(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{linkDraft?.id ? "Edit link" : "Add link"}</DialogTitle>
          </DialogHeader>
          {linkDraft ? (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="link-label" className="text-xs">Label</Label>
                <Input id="link-label" value={linkDraft.label} onChange={(e) => setLinkDraft({ ...linkDraft, label: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="link-href" className="text-xs">Link</Label>
                <Input id="link-href" value={linkDraft.href} onChange={(e) => setLinkDraft({ ...linkDraft, href: e.target.value })} placeholder="/privacy-policy" />
              </div>
              <div className="flex items-center justify-between rounded-md border p-3">
                <span className="text-sm">Open in new tab</span>
                <Switch checked={linkDraft.is_external} onCheckedChange={(v) => setLinkDraft({ ...linkDraft, is_external: v })} />
              </div>
              <div className="flex items-center justify-between rounded-md border p-3">
                <span className="text-sm">Visible on site</span>
                <Switch checked={linkDraft.is_visible} onCheckedChange={(v) => setLinkDraft({ ...linkDraft, is_visible: v })} />
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkDraft(null)}>Cancel</Button>
            <Button
              disabled={!linkDraft?.label.trim() || !linkDraft?.href.trim() || linkMutation.isPending}
              onClick={() => linkDraft && linkMutation.mutate(linkDraft)}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
